import { useCallback, useEffect, useRef, useState } from 'react';
import {
	HOME_BANNER_AUTOPLAY_MS,
	HOME_BANNER_SLIDE_MS,
} from '../../../config/homeBanners.config';

export const useHomeBannerCarousel = (
	slideCount,
	loopSlideCount,
	intervalMs = HOME_BANNER_AUTOPLAY_MS,
) => {
	const [trackIndex, setTrackIndex] = useState(0);
	const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
	const trackIndexRef = useRef(0);
	const isLoopingRef = useRef(false);
	const loopResetTimeoutRef = useRef(null);

	const activeIndex = slideCount > 0 ? trackIndex % slideCount : 0;
	const safeTrackIndex =
		loopSlideCount > 0
			? Math.min(Math.max(trackIndex, 0), loopSlideCount - 1)
			: 0;

	const clearLoopResetTimeout = useCallback(() => {
		if (loopResetTimeoutRef.current) {
			window.clearTimeout(loopResetTimeoutRef.current);
			loopResetTimeoutRef.current = null;
		}
	}, []);

	const resetToStart = useCallback(() => {
		isLoopingRef.current = true;
		clearLoopResetTimeout();
		setIsTransitionEnabled(false);
		setTrackIndex(0);

		window.requestAnimationFrame(() => {
			window.requestAnimationFrame(() => {
				setIsTransitionEnabled(true);
				isLoopingRef.current = false;
			});
		});
	}, [clearLoopResetTimeout]);

	useEffect(() => {
		trackIndexRef.current = trackIndex;
	}, [trackIndex]);

	useEffect(() => {
		setTrackIndex(0);
		setIsTransitionEnabled(true);
		trackIndexRef.current = 0;
		isLoopingRef.current = false;
		clearLoopResetTimeout();
	}, [clearLoopResetTimeout, slideCount]);

	useEffect(() => {
		if (slideCount <= 1) {
			return undefined;
		}

		const timerId = window.setInterval(() => {
			if (isLoopingRef.current) {
				return;
			}

			setIsTransitionEnabled(true);
			setTrackIndex((current) => {
				if (current >= slideCount) {
					return current;
				}

				const nextIndex = current + 1;

				if (nextIndex >= slideCount) {
					clearLoopResetTimeout();
					loopResetTimeoutRef.current = window.setTimeout(() => {
						if (trackIndexRef.current >= slideCount) {
							resetToStart();
						}
					}, HOME_BANNER_SLIDE_MS + 120);
				}

				return nextIndex;
			});
		}, intervalMs);

		return () => {
			window.clearInterval(timerId);
			clearLoopResetTimeout();
		};
	}, [clearLoopResetTimeout, intervalMs, resetToStart, slideCount]);

	const handleTrackTransitionEnd = useCallback(
		(event) => {
			if (slideCount <= 1) {
				return;
			}

			if (event.propertyName !== 'transform') {
				return;
			}

			if (event.target !== event.currentTarget) {
				return;
			}

			if (trackIndexRef.current !== slideCount) {
				return;
			}

			clearLoopResetTimeout();
			resetToStart();
		},
		[clearLoopResetTimeout, resetToStart, slideCount],
	);

	return {
		activeIndex,
		trackIndex: safeTrackIndex,
		isTransitionEnabled,
		handleTrackTransitionEnd,
	};
};
